import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Trash2, Plus } from 'lucide-react';
import api from '../../../connector/api';

import SchoolYearModal from '../modal/SchoolYearModal';
import TermModal from '../modal/TermModal';
import YearTermLinkModal from '../modal/YearTermLinkModal';
import DeleteConfirmModal from '../modal/DeteleConfirmModal';

export default function AcademicCalendarTab() {
    const [schoolYears, setSchoolYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [schoolYearTerms, setSchoolYearTerms] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal Visibility States
    const [activeModal, setActiveModal] = useState(null); // 'year' | 'term' | 'link' | null
    const [selectedItem, setSelectedItem] = useState(null);

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ endpoint: '', id: null, displayName: '' });
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [yearsRes, termsRes, mapRes] = await Promise.all([
                api.get('/academic/years/'),
                api.get('/terms/'),
                api.get('/year-terms/')
            ]);

            setSchoolYears(yearsRes.data.results || yearsRes.data || []);
            setTerms(termsRes.data.results || termsRes.data || []);
            setSchoolYearTerms(mapRes.data.results || mapRes.data || []);
        } catch (err) {
            console.error("Failed to load academic calendar data:", err);
            toast.error("Error loading academic calendar records.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenModal = (type, item = null) => {
        setSelectedItem(item);
        setActiveModal(type);
    };

    const handleCloseModals = () => {
        setActiveModal(null);
        setSelectedItem(null);
    };

    const handleConfirmDelete = (endpoint, id, displayName) => {
        setDeleteTarget({ endpoint, id, displayName });
        setIsDeleteModalOpen(true);
    };

    const handleDeleteExecute = async () => {
        if (!deleteTarget.id) return;
        setIsDeleting(true);
        try {
            await api.delete(`${deleteTarget.endpoint}${deleteTarget.id}/`);
            toast.success(`${deleteTarget.displayName} deleted successfully!`);
            fetchData();
            setIsDeleteModalOpen(false);
        } catch (err) {
            console.error("Failed to delete record:", err);
            toast.error("Failed to delete record.");
        } finally {
            setIsDeleting(false);
            setDeleteTarget({ endpoint: '', id: null, displayName: '' });
        }
    };

    return (
        <div className="space-y-6">
            {/* Header Control Panel */}
            <div className="bg-white border border-zinc-200 rounded-lg p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-900 tracking-tight">
                        Academic Calendar & Terms
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Manage systemic academic calendar years and term structures.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleOpenModal('year')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg hover:bg-zinc-100 transition-colors shadow-xs"
                    >
                        <Plus className="w-3.5 h-3.5" /> Year
                    </button>
                    <button
                        onClick={() => handleOpenModal('term')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg hover:bg-zinc-100 transition-colors shadow-xs"
                    >
                        <Plus className="w-3.5 h-3.5" /> Term
                    </button>
                    <button
                        onClick={() => handleOpenModal('link')}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-3.5 h-3.5" /> Connect
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-center text-xs text-zinc-500 bg-white border border-zinc-200 rounded-xl">
                    Loading academic calendar configuration...
                </div>
            ) : (
                <>
                    {/* Main Operational Data Containers */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* School Years Block */}
                        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-5 shadow-sm space-y-4">
                            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                                School Years
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs text-zinc-700">
                                    <thead className="bg-zinc-50 border-y border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold text-[10px]">
                                        <tr>
                                            <th className="py-2 px-3">Name</th>
                                            <th className="py-2 px-3">Start Date</th>
                                            <th className="py-2 px-3">End Date</th>
                                            <th className="py-2 px-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100">
                                        {schoolYears.length > 0 ? (
                                            schoolYears.map((year) => (
                                                <tr key={year.id} className="hover:bg-zinc-50/50">
                                                    <td className="py-2.5 px-3 font-bold text-zinc-900">{year.name}</td>
                                                    <td className="py-2.5 px-3 font-mono text-zinc-500">{year.start_date}</td>
                                                    <td className="py-2.5 px-3 font-mono text-zinc-500">{year.end_date}</td>
                                                    <td className="py-2.5 px-3 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                onClick={() => handleOpenModal('year', year)}
                                                                title="Edit School Year"
                                                                className="p-1 text-zinc-500 hover:text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-md transition-colors"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleConfirmDelete('/years/', year.id, year.name)}
                                                                title="Delete School Year"
                                                                className="p-1 text-zinc-500 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-md transition-colors"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="py-3 px-3 text-center text-zinc-400 italic">
                                                    No registered school years found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Global Terms Container */}
                        <div className="bg-white border border-zinc-200 rounded-lg p-5 shadow-sm space-y-4">
                            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                                Global Terms
                            </h3>
                            <div className="space-y-2">
                                {terms.length === 0 ? (
                                    <div className="text-center py-4 text-xs text-zinc-400 italic">No terms configured.</div>
                                ) : (
                                    terms.map((term) => (
                                        <div
                                            key={term.id}
                                            className="flex items-center justify-between p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs"
                                        >
                                            <span className="font-semibold text-zinc-800">{term.name}</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleOpenModal('term', term)}
                                                    title="Edit Term"
                                                    className="p-1 text-zinc-500 hover:text-blue-600 hover:bg-white border border-transparent hover:border-zinc-200 rounded-md transition-colors"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleConfirmDelete('/terms/', term.id, term.name)}
                                                    title="Delete Term"
                                                    className="p-1 text-zinc-500 hover:text-rose-600 hover:bg-white border border-transparent hover:border-zinc-200 rounded-md transition-colors"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mapped Connections Block */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-5 shadow-sm space-y-4">
                        <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                            Bound Term Connections
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs text-zinc-700">
                                <thead className="bg-zinc-50 border-y border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold text-[10px]">
                                    <tr>
                                        <th className="py-2 px-3">Term & School Year Scope</th>
                                        <th className="py-2 px-3">Duration Range</th>
                                        <th className="py-2 px-3 text-center">Status</th>
                                        <th className="py-2 px-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100">
                                    {schoolYearTerms.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="py-3 px-3 text-center text-zinc-400 italic">
                                                No term connection scopes mapped.
                                            </td>
                                        </tr>
                                    ) : (
                                        schoolYearTerms.map((syt) => (
                                            <tr key={syt.id} className="hover:bg-zinc-50/50">
                                                <td className="py-2.5 px-3 font-bold text-zinc-900">
                                                    {syt.term?.name}{' '}
                                                    <span className="text-zinc-500 font-normal">
                                                        ({syt.school_year?.name})
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-3 font-mono text-zinc-500">
                                                    {syt.start_date} → {syt.end_date}
                                                </td>
                                                <td className="py-2.5 px-3 text-center">
                                                    {syt.is_current ? (
                                                        <span className="inline-block text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                                            Active Current
                                                        </span>
                                                    ) : (
                                                        <span className="text-zinc-300">—</span>
                                                    )}
                                                </td>
                                                <td className="py-2.5 px-3 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button
                                                            onClick={() => handleOpenModal('link', syt)}
                                                            title="Edit Connection Scope"
                                                            className="p-1 text-zinc-500 hover:text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-md transition-colors"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirmDelete(
                                                                    '/year-terms/',
                                                                    syt.id,
                                                                    `${syt.term?.name} (${syt.school_year?.name})`
                                                                )
                                                            }
                                                            title="Delete Connection Scope"
                                                            className="p-1 text-zinc-500 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-md transition-colors"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Dynamic Modals */}
            {activeModal === 'year' && (
                <SchoolYearModal
                    year={selectedItem}
                    onClose={handleCloseModals}
                    onSuccess={() => {
                        handleCloseModals();
                        fetchData();
                    }}
                />
            )}

            {activeModal === 'term' && (
                <TermModal
                    term={selectedItem}
                    onClose={handleCloseModals}
                    onSuccess={() => {
                        handleCloseModals();
                        fetchData();
                    }}
                />
            )}

            {activeModal === 'link' && (
                <YearTermLinkModal
                    link={selectedItem}
                    schoolYears={schoolYears}
                    terms={terms}
                    onClose={handleCloseModals}
                    onSuccess={() => {
                        handleCloseModals();
                        fetchData();
                    }}
                />
            )}

            {/* Shared Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteExecute}
                title="Confirm Record Deletion"
                message={`Are you sure you want to delete "${deleteTarget.displayName}"? This action cannot be undone.`}
                isDeleting={isDeleting}
            />
        </div>
    );
}